import type { Action, FightCommand, Hand } from '../rules/types';

const holdSeconds = 0.2;
const pull = 0.08;
const bodyHeight = 1 / 3;
// Movement a click may have and still be a click: hands shake.
const wobble = 0.03;

/** One finger or the mouse: where and when it went down, and where it is now. */
interface Touch {
  readonly time: number;
  readonly x: number;
  readonly y: number;
  nowX: number;
  nowY: number;
  pulled: boolean;
  blocking: boolean;
}

/**
 * Mouse or finger gestures to fight commands, with the hand of the half of the screen the press started on. A quick
 * click is a straight punch, or a body blow in the bottom third; pulling sideways is a hook, up an uppercut, down a
 * dodge; holding still is a block, read from `isBlocking` for as long as it is held. Each finger is followed on its own,
 * so two thumbs can hold a block and punch at once. Positions are shares of the screen (0..1, y up), times in seconds.
 */
export class GestureReader {
  private readonly touches = new Map<number, Touch>();

  constructor(private readonly send: (command: FightCommand) => void) {}

  /** Any finger held still long enough blocks. */
  get isBlocking(): boolean {
    for (const touch of this.touches.values()) if (touch.blocking) return true;
    return false;
  }

  press(id: number, time: number, x: number, y: number): void {
    this.touches.set(id, { time, x, y, nowX: x, nowY: y, pulled: false, blocking: false });
  }

  move(id: number, time: number, x: number, y: number): void {
    const touch = this.touches.get(id);
    if (!touch || touch.blocking || touch.pulled) return;
    touch.nowX = x;
    touch.nowY = y;
    const across = x - touch.x;
    const up = y - touch.y;
    // A pull goes whichever way it goes most.
    if (Math.abs(up) >= Math.abs(across) && Math.abs(up) >= pull) this.pull(touch, up < 0 ? 'dodge' : 'uppercut');
    else if (Math.abs(across) > Math.abs(up) && Math.abs(across) >= pull) this.pull(touch, 'hook');
    else this.update(time);
  }

  /** Every frame: a finger down long enough, and still where it went down, becomes a block, whether it moves or not. */
  update(time: number): void {
    for (const touch of this.touches.values()) {
      if (!touch.blocking && !touch.pulled && time - touch.time >= holdSeconds && still(touch, touch.nowX, touch.nowY)) touch.blocking = true;
    }
  }

  release(id: number, time: number, x: number, y: number): void {
    const touch = this.touches.get(id);
    if (!touch) return;
    this.touches.delete(id);
    if (touch.blocking || touch.pulled) return;
    if (time - touch.time < holdSeconds && still(touch, x, y)) this.send({ hand: hand(touch), action: touch.y < bodyHeight ? 'bodyBlow' : 'straight' });
  }

  private pull(touch: Touch, action: Action): void {
    touch.pulled = true;
    this.send({ hand: hand(touch), action });
  }
}

function hand(touch: Touch): Hand {
  return touch.x < 0.5 ? 'left' : 'right';
}

function still(touch: Touch, x: number, y: number): boolean {
  return Math.abs(x - touch.x) < wobble && Math.abs(y - touch.y) < wobble;
}
